import { useState } from "react";
import { Container } from "react-grid-system";
import theme from "@config/theme";
import BlogPost, { formatBlogPublishDate } from "@components/blog/BlogPost";
import FeaturedBlog from "@components/blog/FeaturedBlog";
import { NewsletterContainerDark } from "@components/newsletter/NewsletterContainerDark";
import { FEATURED_TAGS } from "@content/en/blog/Tags";
import Hero from "@components/layout/Hero";
import CardLayout from "@components/layout/CardLayout";
import Tags from "@components/Tags";
import { stringToURI } from "@scripts/utils";
import AuthorMetaData from "./AuthorMetaData";

type BlogPageProps = {
  featuredPost: BlogPost;
  posts: BlogPost[];
};

const ALL_POSTS_LABEL = "All Posts";

const BlogPage = ({ featuredPost, posts }: BlogPageProps) => {
  const notFeaturedPosts = posts.filter((post) => !post.featured);

  const [filteredPosts, setFilteredPosts] = useState(notFeaturedPosts);
  const [activeFilter, setActiveFilter] = useState(ALL_POSTS_LABEL);

  const updatePosts = (category: string) => {
    setActiveFilter(category);
    if (category === ALL_POSTS_LABEL) {
      setFilteredPosts(notFeaturedPosts);
    } else {
      setFilteredPosts(posts.filter((post) => post.tags.includes(category)));
    }
  };

  return (
    <>
      <Hero
        title="Welcome to the Blues Developer Blog"
        subtitle="Technical articles on everything in the Internet of Things, from Arduino to Zephyr. Written by developers, for developers."
        size="compact"
      />
      <Container>
        <div className="blog-page">
          <FeaturedBlog featuredPost={featuredPost} />

          <hr />

          <div className="filters">
            <button
              type="button"
              onClick={() => updatePosts(ALL_POSTS_LABEL)}
              className={activeFilter === ALL_POSTS_LABEL ? "active" : ""}
            >
              {ALL_POSTS_LABEL}
            </button>
            {FEATURED_TAGS.map((tag) => (
              <button
                type="button"
                key={tag}
                onClick={() => updatePosts(tag)}
                className={activeFilter === tag ? "active" : ""}
              >
                {tag}
              </button>
            ))}
          </div>

          <CardLayout
            cards={filteredPosts.map((post) => ({
              link: `/blog/${post.slug}`,
              title: post.title,
              description: post.excerpt,
              banner: post.banner,
              date: formatBlogPublishDate(post.publishedAt),
              descriptionExtra: (
                <Tags
                  tags={post.tags.map((tag) => ({
                    name: tag,
                    url: `/blog/tag/${stringToURI(tag)}`,
                  }))}
                />
              ),

              footerExtra: <AuthorMetaData author={post.author} />,
            }))}
            limit={6}
          />
        </div>
      </Container>

      <div className="blog-newsletter-container">
        <NewsletterContainerDark pageSource="Blog Home Page" />
      </div>
      <style jsx>{`
        .blog-page {
          padding: 0 1rem;
        }
        hr {
          border: none;
          border-top: 1px solid ${theme.colors.gray4};
          margin: 2rem 0;
        }

        .filters {
          margin-bottom: 2rem;
          display: flex;
          justify-content: left;
          gap: 0.5rem;
          flex-wrap: wrap;
        }
        .filters button {
          background: ${theme.colors.white};
          border: 1px solid ${theme.colors.black};
          border-radius: 5px;
          padding: 0.25rem 0.5rem;
          cursor: pointer;
        }
        .filters button:hover {
          background: ${theme.colors.gray5};
        }
        .filters button.active {
          border-color: ${theme.colors.ultramarineTint};
          color: ${theme.colors.ultramarineTint};
        }

        .blog-newsletter-container {
          padding: 3rem 0 0;
        }
      `}</style>
    </>
  );
};

export default BlogPage;
